import { encodeLog, type KeystrokeLog } from '@coderaw/log'
import { CHAR_CAP, countTokens } from '@coderaw/runtime'
import { ApiError, type Api } from '../lib/api'
import { bytesToBase64 } from '../lib/base64'
import { el, replaceChildren } from '../lib/dom'
import { replayPath } from '../router'

const SAVE_ERRORS: Record<string, string> = {
  code_too_long: `Too many characters. The cap is ${CHAR_CAP.toLocaleString('en-US')}.`,
  over_budget: 'Over the token budget. Trim a little.',
  syntax_error: 'The code does not parse yet.',
  bad_log: 'The keystroke log looks damaged. Reload and try again.',
  log_mismatch: 'The keystroke log and the code disagree. Reload and try again.',
  log_too_large: 'The keystroke log is too big to save.',
  unauthorized: 'Log in with GitHub to save.',
}

export const saveError = (error: unknown): string => {
  if (error instanceof ApiError) return SAVE_ERRORS[error.code] ?? error.message
  return 'Could not reach the town. Your code is still here; try again.'
}

/** Why a save cannot start yet, or null when it can. */
export const saveBlocker = (doc: string, loggedIn: boolean): string | null => {
  if (!loggedIn) return 'Log in with GitHub to save.'
  if (doc.trim() === '') return 'Type something first.'
  const count = countTokens(doc)
  if (count.error) return 'The code does not parse yet.'
  if (count.overBudget || count.overCap) return 'Over budget.'
  return null
}

export type SaveFlowOptions = {
  readonly api: Api
  readonly loggedIn: boolean
  readonly prompt: string
  readonly doc: () => string
  readonly log: () => KeystrokeLog
  readonly status: HTMLElement
}

export type SaveFlow = {
  readonly save: () => Promise<void>
  /** The doc as of the last successful save, for the unsaved-draft guard. */
  readonly savedDoc: () => string | null
  readonly busy: () => boolean
}

const copyLink = async (status: HTMLElement, url: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(url)
    status.append(' (copied)')
  } catch {
    // Clipboard is a nicety; the link is on screen.
  }
}

/** Uploads the code and log, then shows (and copies) the replay link. */
export const createSaveFlow = (options: SaveFlowOptions): SaveFlow => {
  const { status } = options
  let savedDoc: string | null = null
  let busy = false

  const save = async (): Promise<void> => {
    if (busy) return
    const blocker = saveBlocker(options.doc(), options.loggedIn)
    if (blocker) {
      status.textContent = blocker
      return
    }
    busy = true
    status.textContent = 'saving…'
    try {
      const code = options.doc()
      const log = bytesToBase64(await encodeLog(options.log()))
      const { id } = await options.api.createVersion({ code, log, prompt: options.prompt })
      savedDoc = code
      const url = `${location.origin}${replayPath(id)}`
      replaceChildren(status, [
        'saved. share it: ',
        el('a', { href: replayPath(id), 'data-link': true }, url),
      ])
      await copyLink(status, url)
    } catch (error) {
      status.textContent = saveError(error)
    } finally {
      busy = false
    }
  }

  return { save, savedDoc: () => savedDoc, busy: () => busy }
}
